// Everything the team drawer's Stats tab shows, derived from data the drawer
// already holds: the team's raw `games` rows (fetchSchedule), its
// team_season_stats via the AuthContext teams map, and that map as a whole for
// the FBS rank and opponents' records. No extra query.
//
// Spread convention matches scoring.js: games.home_spread is from the home
// side, negative means favoured. A team's own line is home_spread at home and
// its negation away.
import { normalizeTeamName } from "./teamName";

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const fmt1 = (n) => (Number.isFinite(n) ? n.toFixed(1) : "—");
const signed = (n) => (Number.isFinite(n) ? `${n > 0 ? "+" : ""}${n.toFixed(1)}` : "—");
const wl = (w, l, t = 0) => (w + l + t === 0 ? "—" : t ? `${w}-${l}-${t}` : `${w}-${l}`);

// "3-1" or "3-1-0" to [wins, losses].
const parseRecord = (rec) => {
  const m = /^(\d+)-(\d+)/.exec(rec || "");
  return m ? [Number(m[1]), Number(m[2])] : null;
};

function perspective(game, slug) {
  const home = game.home_team === slug;
  const own = home ? game.home_score : game.away_score;
  const opp = home ? game.away_score : game.home_score;
  const spread = typeof game.home_spread === "number"
    ? (home ? game.home_spread : -game.home_spread)
    : null;
  return {
    home,
    neutral: Boolean(game.neutral_site),
    opponent: home ? game.away_team : game.home_team,
    own,
    opp,
    spread,
    conference: Boolean(game.conference_game),
    complete: Boolean(game.game_complete) && own != null && opp != null,
    week: parseInt(game.week, 10),
    postseason: game.season_type === "postseason",
  };
}

// Rank by season fantasy total among FBS teams, standard competition ranking
// (two teams on 30 are both 4th, the next is 6th).
function fbsRank(slug, allTeams) {
  const fbs = Object.values(allTeams || {}).filter(
    (t) => (t.classification || "").toLowerCase() === "fbs"
  );
  const mine = fbs.find((t) => normalizeTeamName(t.school) === slug);
  if (!mine) return null;
  const pts = mine.currentSeason?.gamePoints || 0;
  const ahead = fbs.filter((t) => (t.currentSeason?.gamePoints || 0) > pts).length;
  return { rank: ahead + 1, of: fbs.length };
}

export function computeTeamProfileStats({ slug, games = [], team, allTeams }) {
  const season = team?.currentSeason || {};
  const rows = games.map((g) => perspective(g, slug));
  const played = rows.filter((r) => r.complete);

  // Fantasy. Week 0 never scores (espn.js), and a bye writes nothing.
  const weekly = Object.entries(season.weeklyPoints || {})
    .filter(([w, p]) => String(w) !== "0" && typeof p === "number")
    .map(([w, p]) => ({ week: Number(w), points: p }));
  const total = season.gamePoints || 0;
  const best = weekly.length ? weekly.reduce((a, b) => (b.points > a.points ? b : a)) : null;
  const worst = weekly.length ? weekly.reduce((a, b) => (b.points < a.points ? b : a)) : null;
  const positive = weekly.filter((w) => w.points > 0).length;
  const rank = weekly.length ? fbsRank(slug, allTeams) : null;

  // Against the spread, only on games that had a line.
  const lined = played.filter((r) => r.spread != null);
  const coverMargin = (r) => r.own - r.opp + r.spread;
  let covers = 0, misses = 0, pushes = 0;
  const favRec = [0, 0], dogRec = [0, 0];
  lined.forEach((r) => {
    const m = coverMargin(r);
    if (m > 0) covers += 1; else if (m < 0) misses += 1; else pushes += 1;
    const bucket = r.spread < 0 ? favRec : r.spread > 0 ? dogRec : null;
    if (bucket && m !== 0) bucket[m > 0 ? 0 : 1] += 1;
  });
  const avgCover = lined.length ? lined.reduce((s, r) => s + coverMargin(r), 0) / lined.length : NaN;
  const decided = covers + misses;

  // Results.
  const wins = played.filter((r) => r.own > r.opp);
  const tally = (list) => [list.filter((r) => r.own > r.opp).length, list.filter((r) => r.own < r.opp).length];
  const [hw, hl] = tally(played.filter((r) => r.home && !r.neutral));
  const [aw, al] = tally(played.filter((r) => !r.home || r.neutral));
  const pf = played.length ? played.reduce((s, r) => s + r.own, 0) / played.length : NaN;
  const pa = played.length ? played.reduce((s, r) => s + r.opp, 0) / played.length : NaN;
  const avgWinMargin = wins.length ? wins.reduce((s, r) => s + (r.own - r.opp), 0) / wins.length : NaN;
  const avgMargin = played.length ? played.reduce((s, r) => s + (r.own - r.opp), 0) / played.length : NaN;
  const biggestWin = wins.length ? wins.reduce((a, b) => (b.own - b.opp > a.own - a.opp ? b : a)) : null;
  const confGames = played.filter((r) => r.conference);
  const confFallback = confGames.length ? wl(...tally(confGames)) : "—";
  const biggestCover = lined.length ? Math.max(...lined.map(coverMargin)) : NaN;

  // Schedule ahead: regular season games not yet final.
  const remaining = rows.filter((r) => !r.complete && !r.postseason);
  let oppW = 0, oppL = 0;
  remaining.forEach((r) => {
    const opp = allTeams?.[r.opponent];
    const rec = parseRecord(opp?.currentSeason?.record);
    if (rec) { oppW += rec[0]; oppL += rec[1]; }
  });
  const remainingLined = remaining.filter((r) => r.spread != null);
  const favouredAhead = remainingLined.filter((r) => r.spread < 0).length;
  const confLeft = remaining.filter((r) => r.conference).length;
  const homeLeft = remaining.filter((r) => r.home && !r.neutral).length;
  const regularWeeks = rows.filter((r) => !r.postseason && Number.isFinite(r.week)).map((r) => r.week);
  const lastWeek = regularWeeks.length ? Math.max(...regularWeeks) : 0;
  const nextPlayableWeek = remaining.length ? Math.min(...remaining.map((r) => r.week)) : null;
  let nextBye = null;
  if (nextPlayableWeek != null) {
    for (let w = Math.max(1, nextPlayableWeek); w <= lastWeek; w += 1) {
      if (!regularWeeks.includes(w)) { nextBye = w; break; }
    }
  }

  return [
    {
      title: "Fantasy",
      tiles: [
        { label: "Season pts", value: String(total) },
        { label: "FBS rank", value: rank ? ordinal(rank.rank) : "—", note: rank ? `of ${rank.of} teams` : null, tone: rank && rank.rank <= Math.ceil(rank.of / 4) ? "good" : null },
        { label: "Per week", value: weekly.length ? fmt1(total / weekly.length) : "—" },
        { label: "Positive weeks", value: weekly.length ? `${positive} of ${weekly.length}` : "—" },
        { label: "Best week", value: best ? `${best.points > 0 ? "+" : ""}${best.points}` : "—", note: best ? `Week ${best.week}` : null, tone: best && best.points > 0 ? "good" : null },
        { label: "Worst week", value: worst ? `${worst.points > 0 ? "+" : ""}${worst.points}` : "—", note: worst ? `Week ${worst.week}` : null, tone: worst && worst.points < 0 ? "bad" : null },
      ],
    },
    {
      title: "Against the spread",
      tiles: [
        { label: "ATS record", value: wl(covers, misses, pushes) },
        { label: "Cover rate", value: decided ? `${Math.round((covers / decided) * 100)}%` : "—", tone: decided ? (covers / decided >= 0.6 ? "good" : covers / decided <= 0.4 ? "bad" : null) : null },
        { label: "Beats line by", value: signed(avgCover), note: lined.length ? "points a game" : null, tone: avgCover > 0 ? "good" : avgCover < 0 ? "bad" : null },
        { label: "As favourite", value: wl(favRec[0], favRec[1]), note: "ATS" },
        { label: "As underdog", value: wl(dogRec[0], dogRec[1]), note: "ATS" },
        { label: "Biggest cover", value: Number.isFinite(biggestCover) && biggestCover > 0 ? `+${fmt1(biggestCover)}` : "—", note: "points" },
      ],
    },
    {
      title: "Results",
      tiles: [
        { label: "Record", value: season.record || wl(...tally(played)) },
        { label: "Conference", value: season.confRecord || confFallback, note: season.confRecord || confGames.length ? null : "none played" },
        { label: "Home", value: wl(hw, hl) },
        { label: "Away", value: wl(aw, al), note: "incl. neutral" },
        { label: "Scored", value: fmt1(pf), note: "points a game" },
        { label: "Allowed", value: fmt1(pa), note: "points a game" },
        { label: "Margin", value: signed(avgMargin), note: "average, all games", tone: avgMargin > 0 ? "good" : avgMargin < 0 ? "bad" : null },
        { label: "Win margin", value: wins.length ? `+${fmt1(avgWinMargin)}` : "—", note: "average" },
        { label: "Biggest win", value: biggestWin ? `+${biggestWin.own - biggestWin.opp}` : "—", note: biggestWin ? `${biggestWin.own}-${biggestWin.opp}` : null },
      ],
    },
    {
      title: "Schedule ahead",
      tiles: [
        { label: "Games left", value: String(remaining.length) },
        { label: "Opponents", value: oppW + oppL ? wl(oppW, oppL) : "—", note: "combined record" },
        { label: "Favoured in", value: remainingLined.length ? `${favouredAhead} of ${remainingLined.length}` : "—", note: remainingLined.length ? "lined games" : "no lines yet" },
        { label: "Next bye", value: nextBye != null ? `Week ${nextBye}` : "None" },
        { label: "Conference", value: String(confLeft), note: "games left" },
        { label: "At home", value: String(homeLeft), note: "games left" },
      ],
    },
  ];
}
